import React, { forwardRef } from 'react';
import { cloneDeep, isArray, mergeWith } from 'lodash';
import isURL from 'validator/es/lib/isURL';

// Require Editor JS files.
/* eslint-disable import/extensions */
import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/js/plugins/file.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/font_family.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/video.min.js';
import 'froala-editor/js/plugins.pkgd.min.js';
/* eslint-enable import/extensions */

// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/plugins/file.min.css';
import 'froala-editor/css/plugins/image.min.css';
import 'froala-editor/css/plugins/code_view.min.css';
import 'froala-editor/css/plugins/colors.min.css';

import FroalaEditor, { MyComponentProps } from 'react-froala-wysiwyg';
import { message } from 'antd';

interface FroalaDefaultButtonsType {
  moreText: {
    buttons: string[];
  };
  moreParagraph: {
    buttons: string[];
  };
  moreRich: {
    buttons: string[];
  };
  moreMisc: {
    buttons: string[];
    align: string;
    buttonsVisible: number;
  };
}

interface ConfigType {
  pluginsEnabled?: string[];
  toolbarButtons?: FroalaDefaultButtonsType;
}

// https://froala.com/wysiwyg-editor/docs/options#toolbarButtons
const FROALA_DEFAULT_BUTTONS: FroalaDefaultButtonsType = {
  moreText: {
    buttons: [
      'bold',
      'italic',
      'underline',
      'strikeThrough',
      'subscript',
      'superscript',
      'fontFamily',
      'fontSize',
      'textColor',
      'backgroundColor',
      'inlineClass',
      'inlineStyle',
      'clearFormatting',
    ],
  },
  moreParagraph: {
    buttons: [
      'alignLeft',
      'alignCenter',
      'formatOLSimple',
      'alignRight',
      'alignJustify',
      'formatOL',
      'formatUL',
      'paragraphFormat',
      'paragraphStyle',
      'lineHeight',
      'outdent',
      'indent',
      'quote',
    ],
  },
  moreRich: {
    buttons: [
      'insertLink',
      'insertImage',
      'insertFile',
      'insertTable',
      'emoticons',
      'fontAwesome',
      'specialCharacters',
      'embedly',
      'insertHR',
    ],
  },
  moreMisc: {
    buttons: [
      'undo',
      'redo',
      'fullscreen',
      'print',
      'getPDF',
      'spellChecker',
      'selectAll',
      'html',
      'help',
    ],
    align: 'right',
    buttonsVisible: 2,
  },
};

// https://froala.com/wysiwyg-editor/docs/plugins/
const FROALA_DEFAULT_PLUGINS = [
  'align',
  'codeBeautifier',
  'codeView',
  'colors',
  'draggable',
  'embedly',
  'emoticons',
  'entities',
  'file',
  'fontAwesome',
  'fontFamily',
  'fontSize',
  'image',
  'imageManager',
  'inlineStyle',
  'lineBreaker',
  'link',
  'lists',
  'paragraphFormat',
  'paragraphStyle',
  'quote',
  'save',
  'table',
  'video',
  'wordPaste',
];

const mergeToolbarButtons = (
  defaultBtnsConfig: FroalaDefaultButtonsType,
  btnsConfig: Partial<FroalaDefaultButtonsType>,
) => {
  const customizer = (objValue: any, srcValue: any) => {
    if (isArray(objValue)) {
      return srcValue;
    }
    // will be handled by lodash.merge when return undefined
  };
  return mergeWith(
    cloneDeep(defaultBtnsConfig),
    cloneDeep(btnsConfig),
    customizer,
  );
};

const FroalaInput = forwardRef<
  any,
  MyComponentProps & { config?: ConfigType & any }
>((props, ref) => {
  const { tag = 'textarea', model, onModelChange, config = {} } = props;
  const { toolbarButtons, pluginsEnabled, ...restConfig } = config;

  return (
    <FroalaEditor
      tag={tag}
      model={model}
      onModelChange={onModelChange}
      config={{
        attribution: false,
        width: '100%',
        heightMin: '40vh',
        charCounterCount: false,
        linkText: false,
        imageUploadURL: '/upload/froala',
        fileUploadURL: '/upload/froala',
        videoUpload: false,
        toolbarButtons: mergeToolbarButtons(
          FROALA_DEFAULT_BUTTONS,
          toolbarButtons || {},
        ),
        videoInsertButtons: ['videoBack', '|', 'videoByURL'],
        imagePaste: true,
        imageEditButtons: [
          'imageReplace',
          'imageRemove',
          'imageLink',
          'linkRemove',
          '|',
          'imageAlt',
          'imageSize',
        ],
        pluginsEnabled: FROALA_DEFAULT_PLUGINS.concat(pluginsEnabled || []),
        events: {
          'link.beforeInsert': (link: string) => {
            if (
              !isURL(link, {
                // eslint-disable-next-line camelcase
                allow_protocol_relative_urls: true, // Allows for Froala's own autolinking
              })
            ) {
              message.error('Please enter a valid URL.');
              return false;
            }
          },
        },
        ...restConfig,
        key: 'mPD4tC3B1F1G1I2B2sGXd1WWTDSGXYOUKh1KINLb1OG1g1H-8D1B3D3D2E6D1G2B4D4H4==', // API key
      }}
      ref={ref}
    />
  );
});

export default FroalaInput;
